/**
 * DoneActions — /actions/done (TECH-27).
 *
 * Lists the actions the team has marked done. Row click opens a focused
 * dialog with three mutations (undone, set actual value, comments).
 *
 * Follows docs/responsive.md:
 *   - Page shell with xl viewport-lock
 *   - <StatStrip> for KPIs
 *   - <CollapsibleFilterBar> for filters
 *   - Table at xl+, cards below md
 *
 * Filter + sort state persists in URL query params so the view is shareable.
 */
import { useMemo, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useSearchParams } from 'react-router-dom'
import {
  CheckCircle2, XCircle, DollarSign, Activity, User,
  ChevronUp, ChevronDown, Search, Info,
} from 'lucide-react'
import { useDoneActions } from '../hooks/useDoneActions.js'
import { useNow } from '../hooks/useNow.js'
import Tooltip from '../components/shared/Tooltip.jsx'
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx'
import StatStrip from '../components/shared/StatStrip.jsx'
import CollapsibleFilterBar from '../components/shared/CollapsibleFilterBar.jsx'
import ActionDetailDialog from '../components/shared/ActionDetailDialog.jsx'
import {
  CategoryIcon, PillarBadge, UrgencyBadge, LeadTimeBadge,
} from '../components/shared/ActionBadges.jsx'
import { fmtPnl } from '../utils/actionVisuals.js'
import { leadTimeHours } from '../utils/leadTime.js'
import { dcLabel } from '../utils/dc.js'

const SORTABLE = {
  marked_done_at: 'Acted at',
  latest_pnl_estimate: 'Expected',
  actual_value_usd: 'Actual',
  category: 'Category',
  marked_done_by_email: 'User',
  node_id: 'Target',
}

const TIME_RANGES = {
  '24h':  { label: 'Last 24h',  ms: 24 * 3600 * 1000 },
  '7d':   { label: 'Last 7d',   ms: 7 * 24 * 3600 * 1000 },
  '30d':  { label: 'Last 30d',  ms: 30 * 24 * 3600 * 1000 },
  all:    { label: 'All time',  ms: null },
}

const OUTCOME_META = {
  marked_done: {
    label: 'Accepted',
    icon: CheckCircle2,
    color: 'text-emerald-300',
    bg: 'bg-emerald-500/15',
    border: 'border-emerald-500/30',
  },
  marked_dismissed: {
    label: 'Dismissed',
    icon: XCircle,
    color: 'text-slate-400',
    bg: 'bg-white/5',
    border: 'border-white/10',
  },
}

function fmtCurrency(v) {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  const sign = n < 0 ? '-' : ''
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

function fmtTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const today = new Date()
  const sameDay = d.toDateString() === today.toDateString()
  if (sameDay) return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: today.getFullYear() === d.getFullYear() ? undefined : '2-digit' })
}

function deltaTone(expected, actual) {
  if (expected == null || actual == null) return 'text-slate-300'
  const diff = Number(actual) - Number(expected)
  if (Math.abs(diff) < 0.01) return 'text-slate-300'
  return diff >= 0 ? 'text-emerald-400' : 'text-amber-400'
}

export default function DoneActions() {
  const { data, isLoading, isError, error } = useDoneActions()
  const rows = data?.actions ?? []
  const { nowMs } = useNow()

  // URL query-param state — these are the persisted filter/sort knobs.
  const [params, setParams] = useSearchParams()
  const text          = params.get('q') ?? ''
  const userFilter    = params.get('user') ?? ''
  const typeFilter    = params.get('type') ?? 'all'
  const dcFilter      = params.get('dc') ?? 'all'
  const storeFilter   = params.get('store') ?? ''
  const outcomeFilter = params.get('outcome') ?? 'all'   // all | marked_done | marked_dismissed
  const timeRange     = params.get('time') ?? '7d'
  const sortBy        = params.get('sort') ?? 'marked_done_at'
  const sortDir       = params.get('dir') ?? 'desc'

  const update = (k, v) => {
    const next = new URLSearchParams(params)
    if (!v || v === '' || v === 'all') next.delete(k)
    else next.set(k, v)
    setParams(next, { replace: true })
  }

  // Distinct user list for the filter dropdown.
  const allUsers = useMemo(() => {
    const s = new Set()
    rows.forEach((r) => { if (r.marked_done_by_email) s.add(r.marked_done_by_email) })
    return Array.from(s).sort()
  }, [rows])

  const allCategories = useMemo(() => {
    const s = new Set()
    rows.forEach((r) => { if (r.category) s.add(r.category) })
    return Array.from(s).sort()
  }, [rows])

  // Distinct DCs across the loaded set — pulled from the payload's ``dc``
  // field (set by the engine on every action) plus node_id for DC/Supplier
  // rows where the wh_id IS the node_id.
  const allDcs = useMemo(() => {
    const s = new Set()
    rows.forEach((r) => {
      const dc = r.latest_payload?.dc
      if (dc) s.add(dc)
      if (r.node_type === 'DC' || r.node_type === 'Supplier') s.add(r.node_id)
    })
    return Array.from(s).sort()
  }, [rows])

  // Helpers for the per-row predicate match.
  const matchesDc = (r) => {
    if (dcFilter === 'all') return true
    if (r.latest_payload?.dc === dcFilter) return true
    if ((r.node_type === 'DC' || r.node_type === 'Supplier') && r.node_id === dcFilter) return true
    // Lane / REALLOC ids encode the dc at the end after '@' or '→'.
    if (typeof r.node_id === 'string') {
      const sep = r.node_id.includes('→') ? '→' : '@'
      const tail = r.node_id.split(sep).pop()
      if (tail === dcFilter) return true
    }
    return false
  }

  const matchesStore = (r) => {
    if (!storeFilter) return true
    const q = storeFilter.toLowerCase()
    const payloadStore = String(r.latest_payload?.store_id ?? '').toLowerCase()
    const nodeId = String(r.node_id ?? '').toLowerCase()
    return payloadStore.includes(q) || nodeId.includes(q)
  }

  // Filtered + sorted rows.
  const visible = useMemo(() => {
    let xs = rows
    if (text) {
      const q = text.toLowerCase()
      xs = xs.filter((r) => {
        const t = (r.latest_title || '').toLowerCase()
        const n = (r.node_id || '').toLowerCase()
        const c = (r.category || '').toLowerCase()
        return t.includes(q) || n.includes(q) || c.includes(q)
      })
    }
    if (userFilter) xs = xs.filter((r) => r.marked_done_by_email === userFilter)
    if (typeFilter && typeFilter !== 'all') xs = xs.filter((r) => r.category === typeFilter)
    if (dcFilter !== 'all') xs = xs.filter(matchesDc)
    if (storeFilter) xs = xs.filter(matchesStore)
    if (outcomeFilter !== 'all') xs = xs.filter((r) => r.outcome === outcomeFilter)
    if (timeRange !== 'all') {
      const cutoff = Date.now() - TIME_RANGES[timeRange].ms
      xs = xs.filter((r) => {
        const ts = r.marked_done_at ? new Date(r.marked_done_at).getTime() : 0
        return ts >= cutoff
      })
    }
    // Sort
    const dir = sortDir === 'desc' ? -1 : 1
    xs = [...xs].sort((a, b) => {
      const av = a[sortBy]
      const bv = b[sortBy]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' || typeof bv === 'number') {
        return ((Number(av) || 0) - (Number(bv) || 0)) * dir
      }
      return String(av).localeCompare(String(bv)) * dir
    })
    return xs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, text, userFilter, typeFilter, dcFilter, storeFilter, outcomeFilter, timeRange, sortBy, sortDir])

  const toggleSort = (col) => {
    if (sortBy === col) update('dir', sortDir === 'asc' ? 'desc' : 'asc')
    else {
      const next = new URLSearchParams(params)
      next.set('sort', col)
      next.set('dir', col === 'marked_done_at' ? 'desc' : 'asc')
      setParams(next, { replace: true })
    }
  }

  // KPI roll-ups (across the filtered set).
  const stats = useMemo(() => {
    const totalExpected = visible.reduce((s, r) => s + (Number(r.latest_pnl_estimate) || 0), 0)
    const withActual = visible.filter((r) => r.actual_value_usd != null)
    const totalActual = withActual.reduce((s, r) => s + (Number(r.actual_value_usd) || 0), 0)
    const acceptedCount = visible.filter((r) => r.outcome === 'marked_done').length
    const dismissedCount = visible.filter((r) => r.outcome === 'marked_dismissed').length
    return {
      count: visible.length,
      acceptedCount,
      dismissedCount,
      uniqueUsers: new Set(visible.map((r) => r.marked_done_by_email).filter(Boolean)).size,
      totalExpected,
      totalActual,
      hasActualCount: withActual.length,
    }
  }, [visible])

  // Selected row for dialog.
  const [selectedId, setSelectedId] = useState(null)
  const selected = useMemo(
    () => rows.find((r) => r.action_id === selectedId) ?? null,
    [rows, selectedId],
  )

  // If the underlying list updates and the selected row disappears (was undone
  // from a different tab), close the dialog.
  useEffect(() => {
    if (selectedId && !rows.some((r) => r.action_id === selectedId)) {
      setSelectedId(null)
    }
  }, [rows, selectedId])

  const activeSummary = useMemo(() => {
    const bits = []
    if (text) bits.push(`"${text}"`)
    if (userFilter) bits.push(`User: ${userFilter}`)
    if (typeFilter && typeFilter !== 'all') bits.push(typeFilter)
    if (dcFilter !== 'all') bits.push(`DC: ${dcFilter}`)
    if (storeFilter) bits.push(`Store: ${storeFilter}`)
    if (outcomeFilter !== 'all') bits.push(OUTCOME_META[outcomeFilter]?.label || outcomeFilter)
    if (timeRange !== 'all') bits.push(TIME_RANGES[timeRange].label)
    return bits.join(' · ') || null
  }, [text, userFilter, typeFilter, dcFilter, storeFilter, outcomeFilter, timeRange])

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] xl:h-[calc(100vh-4rem)] xl:overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 w-full flex-1 xl:flex xl:flex-col xl:min-h-0">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl font-black text-slate-100 flex items-center gap-2">
            <CheckCircle2 size={20} className="text-cyan-400" />
            Done Actions
            <Tooltip content="Actions the team has marked complete. Click a row to log actuals, add comments, or reopen." placement="bottom" asButton>
              <Info size={15} className="text-slate-500 hover:text-slate-300 cursor-help transition-colors" />
            </Tooltip>
          </h1>
        </div>

        {/* KPIs */}
        <StatStrip
          items={[
            { label: 'Accepted',  value: stats.acceptedCount.toLocaleString(),  icon: CheckCircle2, tone: 'success' },
            { label: 'Dismissed', value: stats.dismissedCount.toLocaleString(), icon: XCircle,      tone: 'slate' },
            { label: 'Expected $ total',  value: fmtCurrency(stats.totalExpected), icon: DollarSign, tone: 'slate' },
            { label: `Actual $ (${stats.hasActualCount})`, value: fmtCurrency(stats.totalActual), icon: Activity, tone: stats.totalActual >= stats.totalExpected ? 'success' : 'warning' },
          ]}
        />

        {/* Filters */}
        <div className="mt-4">
          <CollapsibleFilterBar
            persistKey="aeo:filters:open:done"
            activeSummary={activeSummary}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {/* Free-text search */}
              <div className="relative">
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Search
                </label>
                <Search size={14} className="absolute left-3 top-[34px] text-slate-500 pointer-events-none" />
                <input
                  type="text"
                  value={text}
                  onChange={(e) => update('q', e.target.value)}
                  placeholder="Title, target, category…"
                  className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Store</label>
                <input
                  type="text"
                  value={storeFilter}
                  onChange={(e) => update('store', e.target.value)}
                  placeholder="Store ID (e.g. 1234)"
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50 transition-colors"
                />
              </div>

              <FilterSelect
                label="DC"
                value={dcFilter}
                onChange={(v) => update('dc', v)}
                options={[
                  { value: 'all', label: 'All DCs' },
                  ...allDcs.map((d) => ({ value: d, label: dcLabel(d) })),
                ]}
              />

              <FilterSelect
                label="Outcome"
                value={outcomeFilter}
                onChange={(v) => update('outcome', v)}
                options={[
                  { value: 'all', label: 'Accepted + Dismissed' },
                  { value: 'marked_done', label: 'Accepted' },
                  { value: 'marked_dismissed', label: 'Dismissed' },
                ]}
              />

              <FilterSelect
                label="User"
                value={userFilter}
                onChange={(v) => update('user', v)}
                options={[{ value: '', label: 'All users' }, ...allUsers.map((u) => ({ value: u, label: u }))]}
              />

              <FilterSelect
                label="Type"
                value={typeFilter}
                onChange={(v) => update('type', v)}
                options={[{ value: 'all', label: 'All types' }, ...allCategories.map((c) => ({ value: c, label: c }))]}
              />

              <FilterSelect
                label="Time"
                value={timeRange}
                onChange={(v) => update('time', v)}
                options={Object.entries(TIME_RANGES).map(([k, v]) => ({ value: k, label: v.label }))}
              />
            </div>
          </CollapsibleFilterBar>
        </div>

        {/* Body */}
        <div className="mt-4 overflow-auto min-h-[300px] max-h-[min(70vh,700px)] xl:flex-1 xl:min-h-0 xl:max-h-none rounded-xl border border-white/5 bg-white/[0.02]">
          {isLoading && (
            <div className="p-10 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
          {isError && (
            <div className="p-10 text-center text-sm text-red-400">
              Failed to load Done actions: {error?.message ?? 'Unknown error'}
            </div>
          )}
          {!isLoading && !isError && visible.length === 0 && (
            <div className="p-10 text-center text-sm text-slate-500">
              {rows.length === 0
                ? 'No actions have been marked done yet.'
                : 'No rows match the current filters.'}
            </div>
          )}
          {!isLoading && !isError && visible.length > 0 && (
            <>
              {/* Desktop / compressed: table */}
              <div className="hidden md:block">
                <DoneTable
                  rows={visible}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  onRowClick={(r) => setSelectedId(r.action_id)}
                />
              </div>
              {/* Mobile: rich cards matching ActionCenter's ActionCard layout */}
              <div className="md:hidden p-3 flex flex-col gap-3">
                {visible.map((r) => (
                  <DoneCard
                    key={r.action_id}
                    row={r}
                    now={nowMs}
                    onClick={() => setSelectedId(r.action_id)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {selected && (
        <ActionDetailDialog
          action={doneRowToAction(selected)}
          mode="done"
          onClose={() => setSelectedId(null)}
          doneMeta={{
            outcome: selected.outcome,
            actualValueUsd: selected.actual_value_usd,
            markedDoneAt: selected.marked_done_at,
            markedDoneByEmail: selected.marked_done_by_email,
            markedDoneByName: selected.marked_done_by_name,
          }}
        />
      )}
    </div>
  )
}


/**
 * Flatten a Done row (which carries the engine snapshot under
 * ``latest_payload``) into the action-shape ActionDetailDialog expects.
 * Engine payload keys (target, action_text, justification, store_id, dc,
 * wos, sensitivity_score, urgency_reason, pnl_*, etc.) become top-level
 * fields, and the typed latest_* columns are mapped to their normalised
 * names (``trigger_severity``, ``urgency``, ``event_at``, ``pnl_estimate``).
 */
function doneRowToAction(r) {
  const p = r.latest_payload || {}
  return {
    ...p,
    id:                r.action_id,
    category:          r.category,
    node_type:         r.node_type,
    node_id:           r.node_id,
    pillar:            r.pillar,
    trigger_date:      p.trigger_date || r.event_date,
    trigger_severity:  r.latest_severity,
    urgency:           r.latest_urgency,
    event_at:          r.latest_event_at,
    pnl_estimate:      r.latest_pnl_estimate != null ? Number(r.latest_pnl_estimate) : null,
  }
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-cyan-500/50 transition-colors"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

function SortableHeader({ col, label, sortBy, sortDir, onSort, className = '' }) {
  const active = sortBy === col
  return (
    <th
      scope="col"
      className={`text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 hover:text-slate-300 transition-colors ${active ? 'text-slate-200' : ''}`}
      >
        {label}
        {active && (sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />)}
      </button>
    </th>
  )
}

function OutcomeBadge({ outcome }) {
  const meta = OUTCOME_META[outcome] ?? OUTCOME_META.marked_done
  const Icon = meta.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border whitespace-nowrap ${meta.color} ${meta.bg} ${meta.border}`}>
      <Icon size={10} />
      {meta.label}
    </span>
  )
}

function DoneTable({ rows, sortBy, sortDir, onSort, onRowClick }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-white/5 sticky top-0 z-10">
        <tr>
          <SortableHeader col="outcome"              label="Outcome"  sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <th scope="col" className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
            Title
          </th>
          <SortableHeader col="category"             label="Category" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableHeader col="node_id"              label="Target"   sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableHeader col="marked_done_at"       label="Acted at" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableHeader col="marked_done_by_email" label="User"     sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
          <SortableHeader col="latest_pnl_estimate"  label="Expected" sortBy={sortBy} sortDir={sortDir} onSort={onSort} className="text-right" />
          <SortableHeader col="actual_value_usd"     label="Actual"   sortBy={sortBy} sortDir={sortDir} onSort={onSort} className="text-right" />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <motion.tr
            key={r.action_id}
            onClick={() => onRowClick(r)}
            className="border-t border-white/5 cursor-pointer hover:bg-white/[0.03] transition-colors"
            whileHover={{ scale: 1.0 }}
          >
            <td className="px-4 py-3"><OutcomeBadge outcome={r.outcome} /></td>
            <td className="px-4 py-3 text-slate-200 truncate max-w-[280px]">{r.latest_title || '—'}</td>
            <td className="px-4 py-3 text-slate-300 text-xs">{r.category}</td>
            <td className="px-4 py-3 text-slate-400 text-xs truncate max-w-[160px]">{r.node_id}</td>
            <td className="px-4 py-3 text-slate-300 text-xs whitespace-nowrap">{fmtTime(r.marked_done_at)}</td>
            <td className="px-4 py-3 text-slate-300 text-xs mp-mask truncate max-w-[180px]">{r.marked_done_by_email || '—'}</td>
            <td className="px-4 py-3 text-slate-300 text-right tabular-nums">{fmtCurrency(r.latest_pnl_estimate)}</td>
            <td className={`px-4 py-3 text-right tabular-nums ${deltaTone(r.latest_pnl_estimate, r.actual_value_usd)}`}>
              {fmtCurrency(r.actual_value_usd)}
            </td>
          </motion.tr>
        ))}
      </tbody>
    </table>
  )
}

// Mirrors ActionCenter's <ActionCard> layout (CategoryIcon · uppercase
// action_text · target · P&L right · pillar/urgency/lead-time badge row
// · justification) and adds the Done-specific overlay (outcome badge,
// actual P&L, acted-by / acted-at).
function DoneCard({ row, now, onClick }) {
  const p = row.latest_payload || {}
  const actionText = p.action_text || row.latest_title || row.category
  const target = p.target || row.node_id || '—'
  const expected = row.latest_pnl_estimate
  const actual = row.actual_value_usd

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      onClick={onClick}
      className="glass-card p-3 cursor-pointer transition-colors active:bg-white/[0.04]"
    >
      <div className="flex items-start gap-3">
        <CategoryIcon category={row.category} />
        <div className="flex-1 min-w-0">
          {/* Title + P&L */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-black uppercase tracking-wide text-slate-200">
                {actionText}
              </div>
              <div className="text-xs text-slate-300 mt-0.5 truncate">{target}</div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-sm font-bold text-emerald-400 tabular-nums">{fmtPnl(expected ?? 0)}</div>
              <div className="text-[10px] text-slate-600">Expected</div>
            </div>
          </div>

          {/* Badge row — same primitives as ActionCenter, plus the Outcome chip */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <OutcomeBadge outcome={row.outcome} />
            <PillarBadge pillar={row.pillar} />
            <UrgencyBadge
              urgency={row.latest_urgency}
              reason={p.urgency_reason}
              wos={p.wos}
              leadTimeHours={leadTimeHours(row.latest_event_at, now)}
            />
            <LeadTimeBadge eventAt={row.latest_event_at} now={now} />
          </div>

          {/* Justification (2-line clamp, same as ActionCard) */}
          {p.justification && (
            <p className="text-xs text-slate-400 leading-snug mt-2 line-clamp-2">
              {p.justification}
            </p>
          )}

          {/* Done overlay — actual P&L + acted-by + acted-at */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[10px] text-slate-500">
            <span className={`tabular-nums ${deltaTone(expected, actual)}`}>
              Actual: {actual != null ? fmtPnl(Number(actual)) : 'not set'}
            </span>
            <span className="mp-mask truncate max-w-[200px]">
              · {row.marked_done_by_name || row.marked_done_by_email || '—'}
            </span>
            <span>· {fmtTime(row.marked_done_at)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
